import React, { useState, useEffect } from 'react';
import Modal from './Modal';

const SplitModal = ({ isOpen, onClose, scratchOrder, onSplit }) => {
    const [splits, setSplits] = useState([{ quantity: '' }, { quantity: '' }]);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSplits([{ quantity: '' }, { quantity: '' }]);
            setError('');
        }
    }, [isOpen]);

    const addSplit = () => {
        setSplits([...splits, { quantity: '' }]);
    };

    const removeSplit = (index) => {
        if (splits.length > 2) {
            setSplits(splits.filter((_, i) => i !== index));
        }
    };

    const updateSplitQuantity = (index, value) => {
        // Only allow positive integers
        const cleanValue = value.replace(/[^0-9]/g, '');
        const newSplits = [...splits];
        newSplits[index].quantity = cleanValue;
        setSplits(newSplits);
    };

    const validateSplits = () => {
        const quantities = splits.map(split => parseInt(split.quantity) || 0);
        const totalSplitQuantity = quantities.reduce((sum, qty) => sum + qty, 0);
        const originalQuantity = parseInt(scratchOrder?.quantity) || 0;

        if (quantities.some(qty => qty <= 0)) {
            return 'All split quantities must be greater than 0';
        }

        if (totalSplitQuantity !== originalQuantity) {
            return `Total split quantities (${totalSplitQuantity}) must equal original quantity (${originalQuantity})`;
        }

        return null;
    };

    const handleSubmit = async () => {
        const validationError = validateSplits();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const splitQuantities = splits.map(split => ({ quantity: parseInt(split.quantity) }));
            await onSplit(scratchOrder.id, splitQuantities);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to split scratch order');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatNumber = (num) => Number(num).toLocaleString('en-US');

    if (!scratchOrder) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">Split Scratch Order</h3>

                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <div className="text-sm text-gray-600 mb-2">Original Order Details:</div>
                    <div className="text-sm">
                        <div><strong>Denomination:</strong> IQD {formatNumber(scratchOrder.denomination)}</div>
                        <div><strong>Original Quantity:</strong> {scratchOrder.quantity}</div>
                        <div><strong>Warehouse:</strong> {scratchOrder.warehouse}</div>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Split into quantities (must total {scratchOrder.quantity}):
                    </label>

                    {splits.map((split, index) => (
                        <div key={index} className="flex items-center gap-2 mb-2">
                            <input
                                type="text"
                                value={split.quantity}
                                onChange={(e) => updateSplitQuantity(index, e.target.value)}
                                placeholder="Enter quantity"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                                type="button"
                                onClick={() => removeSplit(index)}
                                disabled={splits.length === 2}
                                className="px-3 py-2 text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                                Remove
                            </button>
                        </div>
                    ))}

                    <button
                        type="button"
                        onClick={addSplit}
                        className="mt-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50"
                    >
                        + Add Split
                    </button>
                </div>

                <div className="mb-4 p-3 bg-blue-50 rounded-md">
                    <div className="text-sm text-blue-800">
                        <strong>Total:</strong> {splits.reduce((sum, split) => sum + (parseInt(split.quantity) || 0), 0)} / {scratchOrder.quantity}
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isSubmitting ? 'Splitting...' : 'Split Order'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default SplitModal;
